import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TruncatedText } from "@/components/ui/truncated-text";
import {
  Eye,
  Pencil,
  Trash2,
  MoreVertical,
  Upload,
  CheckCircle2,
  Activity,
  Circle,
  Hammer,
  Archive,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { getContentTypeLabel, getTestSourceLabel } from "@/utils/ab-test-utils";
import type { ABTest } from "@/types/ab-tests";
import type { HeroAbTestUpdatePayload } from "@/resources/product/product.api";
import type { ABTestWithProduct } from "../ABTestTable";

function getTestDisplayName(test: ABTest): string {
  if (test.name && test.name.trim().length > 0 && !test.name.startsWith("Draft ")) {
    return test.name;
  }
  if (test.testType === "intent") return test.intentShift.to;
  if (test.testType === "keyword") return test.targetKeyword || test.targetCluster || "\u2014";
  if (test.testType === "custom") {
    return test.customAxes && test.customAxes.length > 0
      ? test.customAxes.map(a => a[0].toUpperCase() + a.slice(1)).join(" + ")
      : test.name || "Multi-axis";
  }
  return test.intentShift.to;
}

function getStatusLabel(status: ABTest["status"]): string {
  if (status === "building") return "Drafting";
  if (status === "archived") return "Archived";
  if (status === "draft" || status === "planning" || status === "paused") return "Planning";
  if (status === "completed") return "Complete";
  if (status === "running") return "Running";
  return status;
}

interface ABTestTableRowProps {
  test: ABTestWithProduct;
  showProductColumn: boolean;
  readOnly: boolean;
  productIdProp?: number;
  onUpdateTest?: (args: { productId: number; testId: string; body: HeroAbTestUpdatePayload }) => Promise<void>;
  onResumeDraft?: (test: ABTest) => void;
  onView: (test: ABTest) => void;
  onEdit: (test: ABTest) => void;
  onEditResults: (test: ABTest) => void;
  onUploadResults: (test: ABTestWithProduct) => void;
  onDelete: (test: ABTest) => void;
}

export function ABTestTableRow({
  test,
  showProductColumn,
  readOnly,
  productIdProp,
  onUpdateTest,
  onResumeDraft,
  onView,
  onEdit,
  onEditResults,
  onUploadResults,
  onDelete,
}: ABTestTableRowProps) {
  const scopeFields = test.scopeFields || [test.contentType];
  const scopeLabel = scopeFields.map(f => getContentTypeLabel(f)).join(", ");
  const sourceLabel = getTestSourceLabel(test.source);
  const testName = getTestDisplayName(test);

  const handleRowClick = () => {
    if (test.status === "building" && onResumeDraft && !readOnly) {
      onResumeDraft(test);
      return;
    }
    onView(test);
  };

  const handleEditClick = () => {
    if (test.status === "building" && onResumeDraft) {
      onResumeDraft(test);
      return;
    }
    if (test.outcome) {
      onEditResults(test);
    } else {
      onEdit(test);
    }
  };

  return (
    <div
      className="group flex border-b border-border last:border-b-0 hover:bg-muted/20 items-center cursor-pointer"
      onClick={handleRowClick}
    >
      {showProductColumn && (
        <div className={cn(
          "relative w-[288px] shrink-0 py-2 pl-3 pr-6 flex items-center z-10",
          "sticky left-0 bg-card",
          "before:content-[''] before:pointer-events-none before:absolute before:inset-0 before:bg-surface-tint-soft",
          "before:opacity-0 before:transition-opacity before:duration-150 group-hover:before:opacity-100",
          "after:content-[''] after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0",
          "after:w-2 after:translate-x-full after:bg-gradient-to-r after:from-foreground/10 after:to-transparent",
          "after:opacity-0 after:transition-opacity [[data-scrolled=true]_&]:after:opacity-100",
          "[&>*]:relative [&>*]:z-[1]",
        )}>
          <ProductTableCell
            name={test.heroProductName || "\u2014"}
            asin={test.heroAsin}
            imageUrl={test.heroProductImage}
            size="lg"
            wrapName
            asinLinkToAmazon
          />
        </div>
      )}

      <div className="flex-1 min-w-[160px] py-2 px-3 text-xs font-medium">
        <TruncatedText>{testName}</TruncatedText>
      </div>

      <div className="w-[160px] shrink-0 py-2 px-3">
        <span className={cn("text-xs", test.source ? "text-foreground" : "text-muted-foreground/50")}>{sourceLabel}</span>
      </div>

      <div className="w-[120px] shrink-0 py-2 px-3">
        <span className="text-xs text-foreground">{scopeLabel}</span>
      </div>

      <div className="w-[100px] shrink-0 py-2 px-3 flex items-center gap-1.5">
        {test.status === "completed" ? (
          <CheckCircle2 className="h-4 w-4 text-status-success-strong" strokeWidth={2} />
        ) : test.status === "running" ? (
          <Activity className="h-4 w-4 text-status-info-strong" strokeWidth={2} />
        ) : test.status === "building" ? (
          <Hammer className="h-4 w-4 text-foreground" strokeWidth={2} />
        ) : test.status === "archived" ? (
          <Archive className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
        ) : (
          <Circle className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
        )}
        <span className={cn(
          "text-xs",
          test.status === "completed" && "text-status-success-strong",
          test.status === "running" && "text-status-info-strong",
          test.status === "building" && "text-foreground",
          (test.status === "archived" || (test.status !== "completed" && test.status !== "running" && test.status !== "building")) && "text-muted-foreground",
        )}>
          {getStatusLabel(test.status)}
        </span>
      </div>

      <div className="w-[180px] shrink-0 py-2 px-3">
        {test.outcome && test.outcome !== "inconclusive" ? (
          <Badge className={cn(
            "font-semibold text-[10px] text-foreground whitespace-nowrap border-0",
            test.outcome === "control" && "bg-primary-tint-strong",
            test.outcome === "variant" && "bg-[hsl(185_70%_88%)]",
          )}>
            {test.outcome === "control" ? "Variant A Won" : "Variant B Won"}
            {test.amazonMetrics?.conversion?.diff && ` (${test.amazonMetrics.conversion.diff})`}
            {test.evidenceStrength && test.evidenceStrength.trim().length > 0 && (
              <span className="ml-1 font-normal text-foreground/60">{" — "}{test.evidenceStrength}</span>
            )}
          </Badge>
        ) : test.outcome === "inconclusive" ? (
          <Badge className="font-semibold text-[10px] text-foreground border-0 bg-muted">
            Inconclusive
            {test.evidenceStrength && test.evidenceStrength.trim().length > 0 && (
              <span className="ml-1 font-normal text-foreground/60">{" — "}{test.evidenceStrength}</span>
            )}
          </Badge>
        ) : test.status === "running" && !readOnly ? (
          <Button
            variant="link"
            size="sm"
            className="text-xs h-auto p-0"
            onClick={(e) => { e.stopPropagation(); onUploadResults(test); }}
          >
            {test.amazonMetrics ? "Update Results" : "Upload Results"}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground/50">{"\u2014"}</span>
        )}
      </div>

      <div className="w-[130px] shrink-0 py-2 px-3 text-xs text-foreground whitespace-nowrap">
        {test.amazonMetrics?.conversion
          ? <span>{test.amazonMetrics.conversion.a} / {test.amazonMetrics.conversion.b}</span>
          : <span className="text-muted-foreground/50">{"\u2014"}</span>
        }
      </div>

      <div className="w-[140px] shrink-0 py-2 px-3 text-xs text-foreground whitespace-nowrap">
        {test.amazonMetrics?.sales
          ? <span>{test.amazonMetrics.sales.a} / {test.amazonMetrics.sales.b}</span>
          : <span className="text-muted-foreground/50">{"\u2014"}</span>
        }
      </div>

      <div className="w-[110px] shrink-0 py-2 px-3 text-xs text-foreground whitespace-nowrap">
        {test.startDate || <span className="text-muted-foreground/50">{"\u2014"}</span>}
      </div>

      <div className="w-[110px] shrink-0 py-2 px-3 text-xs text-foreground whitespace-nowrap">
        {test.status === "completed" && test.endDate
          ? test.endDate
          : <span className="text-muted-foreground/50">{"\u2014"}</span>}
      </div>

      <div className="w-[200px] shrink-0 py-2 px-3">
        {test.status === "running" ? (
          <span className="text-xs text-muted-foreground/50">{"\u2014"}</span>
        ) : test.outcome === "variant" && test.variantBSwitchDate ? (
          <span className="text-xs font-medium text-foreground">
            Listing change submitted {new Date(test.variantBSwitchDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
        ) : test.outcome === "control" ? (
          <span className="text-xs text-foreground">None</span>
        ) : (
          <span className="text-xs text-muted-foreground/50">{"\u2014"}</span>
        )}
      </div>

      <div className="w-[120px] shrink-0 py-2 px-3 flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onView(test)}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View test</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {!readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card z-50 rounded-md">
              <DropdownMenuItem onClick={handleEditClick}>
                <Pencil className="h-3.5 w-3.5 mr-2" />{test.outcome ? "Edit Results & Action" : "Edit"}
              </DropdownMenuItem>
              {test.status === "running" && (
                <DropdownMenuItem onClick={() => onUploadResults(test)}>
                  <Upload className="h-3.5 w-3.5 mr-2" />Upload Results
                </DropdownMenuItem>
              )}
              {test.status === "completed" && onUpdateTest && (
                <DropdownMenuItem onClick={() => {
                  const pid = productIdProp ?? test.heroProductId;
                  if (pid == null) return;
                  void onUpdateTest({ productId: pid, testId: test.id, body: { status: "archived" } });
                }}>
                  <Archive className="h-3.5 w-3.5 mr-2" />Archive
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(test)}>
                <Trash2 className="h-3.5 w-3.5 mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
